import React from "react";
import { cn } from "@/lib/utils";

/**
 * Input — text input primitive.
 *
 * Props extras beyond native `<input>`:
 *   • invalid  — sets aria-invalid + red border ring
 *   • size     — sm | md | lg
 *
 * A11y: forwards ref; supports `aria-describedby`, `aria-invalid`.
 *
 * Guardrails: single-color border, no glow ring, no inset gradient.
 */

const SIZES = {
  sm: "h-8  px-2 text-[13px]",
  md: "h-10 px-3 text-[14px]",
  lg: "h-12 px-4 text-[15px]",
};

export const Input = React.forwardRef(function Input(
  {
    className,
    size = "md",
    invalid = false,
    disabled = false,
    type = "text",
    "data-testid": testId,
    ...rest
  },
  ref
) {
  return (
    <input
      ref={ref}
      type={type}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      data-testid={testId}
      className={cn(
        "block w-full rounded-[var(--ds-radius-md)]",
        "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)]",
        "placeholder:text-[var(--ds-color-ink-subtle)]",
        "border",
        invalid
          ? "border-[var(--ds-color-danger)]"
          : "border-[var(--ds-color-border-strong)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)]",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        SIZES[size] || SIZES.md,
        className
      )}
      style={{ transitionDuration: "var(--ds-duration-fast)" }}
      {...rest}
    />
  );
});

Input.displayName = "DSInput";

export default Input;
